import React, { useState, useEffect } from 'react';

import { InputBase, TableHead } from '@material-ui/core';
import { AnswerOption } from 'models/reviewQuestion';
import styled from 'styled-components';
import { Divider1, FlexContainer } from 'styles/common';

import theme from 'constants/theme';

import { Typography10, Typography4 } from 'uiToolkit/Typography';

import LabelStyledSelect from './LabelStyledSelect';
import CommentsCheckboxes from './commentsCheckboxes';
import { Table, TableRow, TableCell, Dash } from './styles';

const FROM_OPTIONS_COUNT = [0, 1, 2, 3, 4, 5];
const TO_OPTIONS_COUNT = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const NumberRatingAnswerType = ({ question, updateQuestion, source, isDuplicating }) => {
  const { answerOptions } = question;

  const [selectedFromVal, setSelectedFromVal] = useState(FROM_OPTIONS_COUNT[0]);
  const [selectedToVal, setSelectedToVal] = useState(TO_OPTIONS_COUNT[4]);

  const updateNumberOfOptions = (minVal, maxVal) => {
    const optionsCount = maxVal - minVal + 1;
    const data = [];
    for (let i = 0; i < optionsCount; i++) {
      data.push(AnswerOption.fromJSON({ score: i + minVal, displayOrder: i + 1, description: '' }));
    }
    updateQuestion('answerOptions', data);
  };

  const handleChange = (param, value) => updateQuestion(param, value);

  // update answer options to as per default values once - for new question only
  useEffect(() => {
    if (answerOptions?.length !== 0) {
      setSelectedFromVal(answerOptions?.[0]?.score ?? 1);
      setSelectedToVal(answerOptions?.[answerOptions.length - 1]?.score ?? 5);
    } else {
      updateNumberOfOptions(FROM_OPTIONS_COUNT[0], TO_OPTIONS_COUNT[4]);
    }
  }, []);

  // for answer type change
  useEffect(() => {
    if (question.resetDataVar) {
      updateNumberOfOptions(FROM_OPTIONS_COUNT[0], TO_OPTIONS_COUNT[4]);
      setSelectedFromVal(FROM_OPTIONS_COUNT[0]);
      setSelectedToVal(TO_OPTIONS_COUNT[4]);
    }
  }, [question.resetDataVar]);

  const handleMaxOptionsChange = (maxVal) => {
    updateNumberOfOptions(selectedFromVal, maxVal);
    setSelectedToVal(maxVal);
  };
  const handleMinOptionsChange = (minVal) => {
    updateNumberOfOptions(minVal, selectedToVal);
    setSelectedFromVal(minVal);
  };

  const handleAnswerOptionsChange = (index, param, value) => {
    const data = [
      ...answerOptions.slice(0, index),
      { ...answerOptions[index], [param]: value || '' },
      ...answerOptions.slice(index + 1),
    ];
    updateQuestion('answerOptions', data);
  };

  return (
    <Container>
      <FlexContainer style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography4 customColor={theme.palette.text.dark1} topGutter={1}>
          Select options values
        </Typography4>
        <FlexContainer>
          <LabelStyledSelect
            selectedValue={selectedFromVal}
            options={FROM_OPTIONS_COUNT}
            handleOptionsChange={handleMinOptionsChange}
            labelText="From"
            disabled={source === 'engagement' && question?.isEditable === false && !isDuplicating}
          />

          <LabelStyledSelect
            selectedValue={selectedToVal}
            options={TO_OPTIONS_COUNT}
            handleOptionsChange={handleMaxOptionsChange}
            labelText="To"
            disabled={source === 'engagement' && question?.isEditable === false && !isDuplicating}
          />
        </FlexContainer>
      </FlexContainer>
      <Divider1 topGutter={3} bottomGutter={3} />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography10 customColor={theme.palette.text.dark1} align="left" topGutter={0.5} bottomGutter={0.5}>
                Value
              </Typography10>
            </TableCell>
            <TableCell>
              <Typography10 customColor={theme.palette.text.dark1} align="left" topGutter={0.5} bottomGutter={0.5}>
                Description
              </Typography10>
            </TableCell>
            <TableCell>
              <Typography10 customColor={theme.palette.text.dark1} align="left" topGutter={0.5} bottomGutter={0.5}>
                Score
              </Typography10>
            </TableCell>
          </TableRow>
        </TableHead>
        {answerOptions &&
          answerOptions.map((option, i) => {
            const isStartOrEndOption = i === 0 || i === answerOptions.length - 1;

            return (
              <TableRow>
                <TableCell style={{ width: '40px' }}>{i + 1}</TableCell>
                <TableCell>
                  {isStartOrEndOption ? (
                    <StyledInputBase
                      type="text"
                      value={option.description}
                      placeholder="Add description"
                      onChange={(e) => handleAnswerOptionsChange(i, 'description', e.target.value)}
                      disabled={source === 'engagement' && question?.isEditable === false && !isDuplicating}
                    />
                  ) : (
                    <Dash />
                  )}
                </TableCell>
                <TableCell style={{ width: '75px' }}>
                  <StyledInputBase type="number" value={option.score} placeholder="Score" readOnly />
                </TableCell>
              </TableRow>
            );
          })}
      </Table>
      <CommentsCheckboxes
        question={question}
        handleQuestionChange={handleChange}
        source={source}
        isDuplicating={isDuplicating}
      />
    </Container>
  );
};

const Container = styled.div`
  margin-top: 2rem;
`;

const StyledInputBase = styled(InputBase)`
  width: 100%;
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
    text-align: center;
    font-weight: 600;
  }
  input {
    color: #333;
  }
  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
    opacity: 0;
  `};
`;

export default NumberRatingAnswerType;
